import {
  FetchParameterWiseResponse,
  ParameterWiseReport,
  ParsedFetchParameterWiseResponse,
  FetchedScoreWiseReportRequest,
  FetchScoreWiseResponse,
  ParsedFetchScoreWiseResponse,
  FetchedMedalWiseReportRequest,
  FetchMedalWiseResponse,
  ParsedFetchMedalWiseResponse,
  FetchedTargetVsAchievementReportRequest,
  FetchTargetVsAchievementResponse,
  ParsedFetchTargetVsAchievementResponse,
  FetchedAttendanceReportRequest,
  FetchAttendanceResponse,
  ParsedFetchAttendanceResponse,
  FetchedEnrolmentReportRequest,
  FetchEnrolmentResponse,
  ParsedFetchEnrolmentResponse,
  FetchedDropOutReportRequest,
  FetchDropOutResponse,
  ParsedFetchDropOutResponse,
  FetchedPlacementReportRequest,
  FetchPlacementResponse,
  ParsedFetchPlacementResponse,
  FetchedDemandDrivenPlacementReportRequest,
  FetchDemandDrivenPlacementResponse,
  ParsedFetchDemandDrivenPlacementResponse,
  FetchedRetentionReportRequest,
  FetchRetentionResponse,
  ParsedFetchRetentionResponse,
  FetchedOperationsFinanceReportRequest,
  FetchOperationsFinanceResponse,
  ParsedFetchOperationsFinanceResponse,
  FetchedOperationsHRReportRequest,
  FetchOperationsHRResponse,
  ParsedFetchOperationsHRResponse,
  FetchedGovernanceAndEthicsReportRequest,
  FetchGovernanceAndEthicsResponse,
  ParsedFetchGovernanceAndEthicsResponse,
  FetchedSessionQualityReportRequest,
  FetchSessionQualityResponse,
  ParsedFetchSessionQualityResponse,
  FetchedStakeholdersFeedbackReportRequest,
  FetchStakeholdersFeedbackResponse,
  ParsedFetchStakeholdersFeedbackResponse,
  FetchedCenterVisitBrandingReportRequest,
  FetchCenterVisitBrandingResponse,
  ParsedFetchCenterVisitBrandingResponse,
  FetchedStaffTrainingReportRequest,
  FetchStaffTrainingResponse,
  ParsedFetchStaffTrainingResponse,
  FetchedAttendanceSummaryReportRequest,
  ParsedFetchAttendanceSummaryResponse,
  FetchAttendanceSummaryResponse,
  ManageAwardInput,
  ManageAwardOutputArray,
} from "../types/reports";

const reportsParser: any = {};
const parseDate = (dateString:any) => {
  const [day, month, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day); // Month is 0-based in JavaScript
};

reportsParser.fetchedParameterWise = (
  response: FetchParameterWiseResponse
): ParsedFetchParameterWiseResponse => {
  let data: ParameterWiseReport[] = [];
  if (
    response.success &&
    response.data &&
    Array.isArray(response.data?.parameterWiseData) &&
    response.data.parameterWiseData?.length > 0
  ) {
    data = response.data?.parameterWiseData?.map((row: any, index: number) => {
      return {
        id: row.id || `report-${index}`,
        parameter: row.parameter ?? "",
        centre: row.center ?? "",
        quarter1: row.Q1 ?? 0,
        quarter2: row.Q2 ?? 0,
        quarter3: row.Q3 ?? 0,
        quarter4: row.Q4 ?? 0,
      };
    });
  }
  const total = response?.data?.totalRecords ?? 0;
  const reportRows = data.map((report: ParameterWiseReport) => ({
    id: report.id,
    parameter: report.parameter,
    centre: report.centre,
    quarter1: report.quarter1,
    quarter2: report.quarter2,
    quarter3: report.quarter3,
    quarter4: report.quarter4,
  }));
  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedScoreWise = (
  response: FetchScoreWiseResponse
): ParsedFetchScoreWiseResponse => {
  let data: FetchedScoreWiseReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response?.data?.data) &&
    response.data.data.length > 0
  ) {
    data = response.data.data.map((row: any, index: number) => {
      const quarter1 = row.Q1 ?? 0;
      const quarter2 = row.Q2 ?? 0;
      const quarter3 = row.Q3 ?? 0;
      const quarter4 = row.Q4 ?? 0;

      return {
        id: `report-${index + 1}`,
        centre: row.center ?? "",
        quarter1,
        quarter2,
        quarter2Trend:
          quarter1 < quarter2 ? "up" : quarter1 > quarter2 ? "down" : "neutral",
        quarter3,
        quarter3Trend:
          quarter2 < quarter3 ? "up" : quarter2 > quarter3 ? "down" : "neutral",
        quarter4,
        quarter4Trend:
          quarter3 < quarter4 ? "up" : quarter3 > quarter4 ? "down" : "neutral",
      };
    });
  }

  const total = response?.data?.totalRecords ?? 0;

  return {
    reports: data,
    totalReports: total,
  };
};

reportsParser.fetchedMedalWise = (
  response: FetchMedalWiseResponse
): ParsedFetchMedalWiseResponse => {
  let data: FetchedMedalWiseReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.data) &&
    response.data.data.length > 0
  ) {
    data = response.data.data.map((row: any, index: number) => {
      return {
        id: `report-${index}`,
        parameter: row.parameter || "",
        centre: row.center ?? "",
        quarter1: row.Q1 ?? null,
        quarter2: row.Q2 ?? null,
        quarter3: row.Q3 ?? null,
        quarter4: row.Q4 ?? null,
      };
    });
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map((report: FetchedMedalWiseReportRequest) => ({
    id: report.id,
    centre: report.centre,
    quarter1: report.quarter1,
    quarter2: report.quarter2,
    quarter3: report.quarter3,
    quarter4: report.quarter4,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

// Attendance
reportsParser.fetchedAttendance = (
  response: FetchAttendanceResponse
): ParsedFetchAttendanceResponse => {
  let data: FetchedAttendanceReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.data) &&
    response.data.data.length > 0
  ) {
    data = response.data.data.map((row: any, index: number) => ({
      id: row.sixerClassId || `report-${index}`,
      centre: row.centerName ?? "",
      project: row.project ?? "",
      batch: row.batch ?? "",
      batchStartDate: parseDate(row.batchStartDate) ?? "",
      batchEndDate: parseDate(row.batchEndDate) ?? "",
      name: row.name ?? "",
      gender: row.gender ?? "",
      attendancePresent: row.attendancePresent ?? 0,
      attendanceSessions: row.attendanceSessionHappened ?? 0,
      attendancePercentage:Number(parsePercentage(row.attendancePercentage )) ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;
  const reportRows = data.map((report: FetchedAttendanceReportRequest) => ({
    id: report.id,
    centre: report.centre,
    project: report.project,
    batch: report.batch,
    batchStartDate: report.batchStartDate,
    batchEndDate: report.batchEndDate,
    name: report.name,
    gender: report.gender,
    attendancePresent: report.attendancePresent,
    attendanceSessions: report.attendanceSessions,
    attendancePercentage: report.attendancePercentage,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

// Target Vs Achievement
reportsParser.fetchedTargetVsAchievement = (
  response: FetchTargetVsAchievementResponse
): ParsedFetchTargetVsAchievementResponse => {
  let data: FetchedTargetVsAchievementReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.data) &&
    response.data.data.length > 0
  ) {
    data = response.data.data.map((row: any, index: number) => ({
      id: row.targetParameterId || `report-${index}`,
      parameter: row.parameterName ?? "",
      centre: row.center ?? "",
      q1Target: row.Q1_weightage ?? 0,
      q1Achieved: row.Q1_scoreAchived ?? 0,
      q2Target: row.Q2_weightage ?? 0,
      q2Achieved: row.Q2_scoreAchived ?? 0,
      q3Target: row.Q3_weightage ?? 0,
      q3Achieved: row.Q3_scoreAchived ?? 0,
      q4Target: row.Q4_weightage ?? 0,
      q4Achieved: row.Q4_scoreAchived ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map(
    (report: FetchedTargetVsAchievementReportRequest) => ({
      id: report.id,
      parameter: report.parameter,
      centre: report.centre,
      q1Target: report.q1Target,
      q1Achieved: report.q1Achieved,
      q2Target: report.q2Target,
      q2Achieved: report.q2Achieved,
      q3Target: report.q3Target,
      q3Achieved: report.q3Achieved,
      q4Target: report.q4Target,
      q4Achieved: report.q4Achieved,
    })
  );

  return {
    reports: reportRows,
    totalReports: total,
  };
};

// Attendance Summary Report
reportsParser.fetchedAttendanceSummaryReports = (
  response: FetchAttendanceSummaryResponse
): ParsedFetchAttendanceSummaryResponse => {
  let data: FetchedAttendanceSummaryReportRequest[] = [];
  if (
    response.success &&
    response.data &&
    Array.isArray(response?.data?.targetAchievements) &&
    response?.data?.targetAchievements?.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centre: row.centerName ?? "",
      funder: row.funderName ?? "",
      batchId: row.batch ?? "",
      batchStartDate: row.batchStartDate ?? "",
      batchEndDate: row.batchEndDate ?? "",
      lessThanTwentyPercent: row.lessThan19 ?? 0,
      twentyToFiftyPercent: row.between20and50 ?? 0,
      fiftyToSeventyFivePercent: row.between50and75 ?? 0,
      seventyFiveToNintyPercent: row.between75and90 ?? 0,
      greaterThanNintyPercent: row.greaterThan90 ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map(
    (report: FetchedAttendanceSummaryReportRequest) => ({
      id: report.id,
      centre: report.centre,
      funder: report.funder,
      batchId: report.batchId,
      batchStartDate: report.batchStartDate,
      batchEndDate: report.batchEndDate,
      lessThanTwentyPercent: report.lessThanTwentyPercent,
      twentyToFiftyPercent: report.twentyToFiftyPercent,
      fiftyToSeventyFivePercent: report.fiftyToSeventyFivePercent,
      seventyFiveToNintyPercent: report.seventyFiveToNintyPercent,
      greaterThanNintyPercent: report.greaterThanNintyPercent,
    })
  );

  return {
    reports: reportRows,
    totalReports: total,
  };
};

//Enrolment
reportsParser.fetchedEnrolment = (
  response: FetchEnrolmentResponse
): ParsedFetchEnrolmentResponse => {
  let data: FetchedEnrolmentReportRequest[] = [];
  if (
    response.success &&
    response.data &&
    Array.isArray(response?.data?.targetAchievements) &&
    response?.data?.targetAchievements?.length > 0
  ) {
    data = response?.data?.targetAchievements?.map(
      (row: any, index: number) => ({
        id: row.id || `report-${index}`,
        centre: row.centerName ?? "",
        funder: row.funderName ?? "",
        batchId: row.batch ?? "",
        batchStartDate: parseDate(row.batchStartDate) ?? "",
        batchEndDate: parseDate(row.batchEndDate) ?? "",
        totalNumberOfYPsEnrolled: row.youthEnrollment ?? 0,
        totalNumberOfFemaleYPsEnrolled: row.femaleEnrollment ?? 0,
      })
    );
  }
  const total = response?.data?.totalRecords ?? 0;
  const reportRows = data.map((report: FetchedEnrolmentReportRequest) => ({
    id: report.id,
    centre: report.centre,
    funder: report.funder,
    batchId: report.batchId,
    batchStartDate: report.batchStartDate,
    batchEndDate: report.batchEndDate,
    totalNumberOfYPsEnrolled: report.totalNumberOfYPsEnrolled,
    totalNumberOfFemaleYPsEnrolled: report.totalNumberOfFemaleYPsEnrolled,
  }));
  return {
    reports: reportRows,
    totalReports: total,
  };
};
//DropOut
reportsParser.fetchedDropOut = (
  response: FetchDropOutResponse
): ParsedFetchDropOutResponse => {
  let data: FetchedDropOutReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centre: row.centerName ?? "",
      funder: row.funderName ?? "",
      batchId: row.batch ?? "",
      batchStartDate: parseDate(row.batchStartDate) ?? "",
      batchEndDate: parseDate(row.batchEndDate) ?? "",
      totalNumberOfYPsEnrolled: row.totalYouthEnrollment ?? 0,
      totalNumberOfYPsDroppedOut: row.totalYouthDropout ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map((report: FetchedDropOutReportRequest) => ({
    id: report.id,
    centre: report.centre,
    funder: report.funder,
    batchId: report.batchId,
    batchStartDate: report.batchStartDate,
    batchEndDate: report.batchEndDate,
    totalNumberOfYPsEnrolled: report.totalNumberOfYPsEnrolled,
    totalNumberOfYPsDroppedOut: report.totalNumberOfYPsDroppedOut,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

//Placement
reportsParser.fetchedPlacement = (
  response: FetchPlacementResponse
): ParsedFetchPlacementResponse => {
  let data: FetchedPlacementReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centre: row.centerName ?? "",
      funder: row.funderName ?? "",
      batchId: row.batch ?? "",
      batchStartDate: parseDate(row.batchStartDate) ?? "",
      batchEndDate: parseDate(row.batchEndDate) ?? "",
      noOfYPsEnrolled: row.youthEnrollment ?? 0,
      noOfYPsPlaced: row.youthPlacement ?? 0,
      totalYPsPlacedWithin45Days: row.youthPlacement45Days ?? 0,
      totalYPsPlacedWithin60Days: row.youthPlacement60Days ?? 0,
      averageSalary: row.averageSalary ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map((report: FetchedPlacementReportRequest) => ({
    id: report.id,
    centre: report.centre,
    funder: report.funder,
    batchId: report.batchId,
    batchStartDate: report.batchStartDate,
    batchEndDate: report.batchEndDate,
    noOfYPsEnrolled: report.noOfYPsEnrolled,
    noOfYPsPlaced: report.noOfYPsPlaced,
    totalYPsPlacedWithin45Days: report.totalYPsPlacedWithin45Days,
    totalYPsPlacedWithin60Days: report.totalYPsPlacedWithin60Days,
    averageSalary: report.averageSalary,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedDemandDrivenPlacement = (
  response: FetchDemandDrivenPlacementResponse
): ParsedFetchDemandDrivenPlacementResponse => {
  let data: any[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`, 
      centre: row.centerName ?? "N/A", 
      funder: row.funderName ?? "N/A",
      batchId: row.batch ?? "N/A",
      batchStartDate: row.batchStartDate ? parseDate(row.batchStartDate) : null,
      batchEndDate: row.batchEndDate ? parseDate(row.batchEndDate) : null,
      batchType: row.batchType ?? "N/A",
      noOfYPsEnrolled: row.youthEnrollment ?? 0,
      noOfYPsPlaced: row.youthPlacement ?? 0,
      youthPlacementWithLeadEmployer: row.youthPlacementWithLeadEmployer ?? 0,
    }));
    
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map(
    (report: FetchedDemandDrivenPlacementReportRequest) => ({
      id: report.id,
      centre: report.centre,
      funder: report.funder,
      batchId: report.batchId,
      batchStartDate: report.batchStartDate,
      batchEndDate: report.batchEndDate,
      batchType: report.batchType,
      noOfYPsEnrolled: report.noOfYPsEnrolled,
      noOfYPsPlaced: report.noOfYPsPlaced,
      youthPlacementWithLeadEmployer: report.youthPlacementWithLeadEmployer,
    })
  );

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedRetention = (
  response: FetchRetentionResponse
): ParsedFetchRetentionResponse => {
  let data: any[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({  
      id: row.id || `report-${index}`, 
      centre: row.centerName ?? "N/A", 
      funder: row.funderName ?? "N/A",
      batchId: row.batch ?? "N/A",
      batchStartDate: row.batchStartDate ? parseDate(row.batchStartDate) : null,
      batchEndDate: row.batchEndDate ? parseDate(row.batchEndDate) : null,
      batchType: row.batchType ?? "N/A",
      noOfYPsEnrolled: row.youthEnrollment ?? 0,
      noOfYPsPlaced: row.youthPlacement ?? 0,
      YPsRetainedAtEndOf3Months: row.youthRetention ?? 0,
    }));
  }
  

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map((report: FetchedRetentionReportRequest) => ({
    id: report.id,
    centre: report.centre,
    funder: report.funder,
    batchId: report.batchId,
    batchStartDate: report.batchStartDate,
    batchEndDate: report.batchEndDate,
    batchType: report.batchType,
    noOfYPsEnrolled: report.noOfYPsEnrolled,
    noOfYPsPlaced: report.noOfYPsPlaced,
    YPsRetainedAtEndOf3Months: report.YPsRetainedAtEndOf3Months,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

const parsePercentage = (percentageString: string): string => {
  return percentageString.replace("%", ""); // Removes the '%' symbol
};
reportsParser.fetchedOperationsFinance = (
  response: FetchOperationsFinanceResponse
): ParsedFetchOperationsFinanceResponse => {
  let data: FetchedOperationsFinanceReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centre: row.centerName ?? "",
      region: row.region ?? "",
      funder: row.funderName ?? "",
      centreBusinessType: row.centerBusinessType ?? "",
      state: row.state ?? "",
      district: row.district ?? "",
      cityManager: row.cityManager ?? "",
      status: row.status ?? "",
      projectBudgetForQuarter: row.projectBudgetForQuarter ?? "0",
      projectExpensesForQuarter: row.projectExpensesForQuarter ?? "0",
      centerUnderspent: Number(row.centerUnderSpend) ?? "0",
      underSpentPercentage: Number(parsePercentage(row.underSpendPercent ))?? "0",
    }));
  }

  // Total records
  const total = response?.data?.totalRecords ?? 0;

  // Map to final report rows
  const reportRows = data.map(
    (report: FetchedOperationsFinanceReportRequest) => ({
      id: report.id,
      centre: report.centre,
      region: report.region,
      funder: report.funder,
      centreBusinessType: report.centreBusinessType,
      state: report.state,
      district: report.district,
      cityManager: report.cityManager,
      status: report.status,
      projectBudgetForQuarter: report.projectBudgetForQuarter,
      projectExpensesForQuarter: report.projectExpensesForQuarter,
      centerUnderspent: report.centerUnderspent,
      underSpentPercentage: report.underSpentPercentage,
    })
  );

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedOperationsHR = (
  response: FetchOperationsHRResponse
): ParsedFetchOperationsHRResponse => {
  let data: FetchedOperationsHRReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      region: row.region ?? "",
      funder: row.funderName ?? "",
      centre: row.centerName ?? "",
      centreBusinessType: row.centerBusinessType ?? "",
      state: row.state ?? "",
      district: row.district ?? "",
      cityManager: row.cityManager ?? "",
      status: row.status ?? "",
      budgetedStaff: row.budgetedStaff ?? 0,
      staffBeginningQuarter: row.staffAtQuarterStart ?? 0,
      staffEndQuarter: row.staffAtQuarterEnd ?? 0,
      staffToBeHired: row.staffToBeHired ?? 0,
      onboardedStaff: row.onboardedStaff ?? 0,
      empResignedQuarter: row.resignedStaff ?? 0,
      infantAttrition: row.infantAttrition ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map((report: FetchedOperationsHRReportRequest) => ({
    id: report.id,
    region: report.region,
    funder: report.funder,
    centre: report.centre,
    centreBusinessType: report.centreBusinessType,
    state: report.state,
    district: report.district,
    cityManager: report.cityManager,
    status: report.status,
    budgetedStaff: Number(report.budgetedStaff),
    staffBeginningQuarter: report.staffBeginningQuarter,
    staffEndQuarter: report.staffEndQuarter,
    staffToBeHired: report.staffToBeHired,
    onboardedStaff: report.onboardedStaff,
    empResignedQuarter: report.empResignedQuarter,
    infantAttrition: report.infantAttrition,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedGovernanceAndEthics = (
  response: FetchGovernanceAndEthicsResponse
): ParsedFetchGovernanceAndEthicsResponse => {
  let data: FetchedGovernanceAndEthicsReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centreName: row.centerName ?? "",
      region: row.region ?? "",
      funder: row.funderName ?? "",
      state: row.state ?? "",
      district: row.district ?? "",
      cityManager: row.cityManager ?? "",
      status: row.status ?? "",
      centreBusinessType: row.centerBusinessType ?? "",
      centerOccupancy: row.centreOccupancy ?? 0,
      youthAttendance: row.youthAttendance ?? "",
      communication: Number(row.communicationWithFPD )?? "",
      qualityDocumentation: row.qualityDocumentation ?? "",
      remarks: row.remarks ?? "",
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map(
    (report: FetchedGovernanceAndEthicsReportRequest) => ({
      id: report.id,
      centreName: report.centreName,
      region: report.region,
      funder: report.funder,
      state: report.state,
      district: report.district,
      cityManager: report.cityManager,
      status: report.status,
      centreBusinessType: report.centreBusinessType,
      centerOccupancy: report.centerOccupancy,
      youthAttendance: report.youthAttendance,
      communication: report.communication,
      qualityDocumentation: report.qualityDocumentation,
      remarks: report.remarks,
    })
  );

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedSessionQuality = (
  response: FetchSessionQualityResponse
): ParsedFetchSessionQualityResponse => {
  let data: FetchedSessionQualityReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centreName: row.centerName ?? "",
      region: row.region ?? "",
      funder: row.funderName ?? "",
      centreBusinessType: row.centerBusinessType ?? "",
      state: row.state ?? "",
      district: row.district ?? "",
      cityManager: row.cityManager ?? "",
      status: row.status ?? "",
      tcName: row.tcName ?? "",
      tcJoiningDate: row.tcJoiningDate ?? "",
      dateOfObservation: row.dateOfObservation ?? "",
      tcEmplId: row.tcEmplId ?? "",
      knowledge: row.knowledge ?? "",
      skills: row.skills ?? "",
      attitude: row.attitude ?? "",
      assessorName: row.assessorName ?? "",
      remarks: row.remarks ?? "",
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map((report: FetchedSessionQualityReportRequest) => ({
    id: report.id,
    centreName: report.centreName,
    region: report.region,
    funder: report.funder,
    centreBusinessType: report.centreBusinessType,
    state: report.state,
    district: report.district,
    cityManager: report.cityManager,
    status: report.status,
    tcName: report.tcName,
    tcJoiningDate: report.tcJoiningDate,
    dateOfObservation: report.dateOfObservation,
    tcEmplId: report.tcEmplId,
    knowledge: report.knowledge,
    skills: report.skills,
    attitude: report.attitude,
    assessorName: report.assessorName,
    remarks: report.remarks,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedStakeholdersFeedback = (
  response: FetchStakeholdersFeedbackResponse
): ParsedFetchStakeholdersFeedbackResponse => {
  let data: FetchedStakeholdersFeedbackReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      region: row.region ?? "",
      funder: row.funderName ?? "",
      centre: row.centerName ?? "",
      centreBusinessType: row.centerBusinessType ?? "",
      state: row.state ?? "",
      district: row.district ?? "",
      cityManager: row.cityManager ?? "",
      centreStatus: row.status ?? "",
      stakeholderFeedbackScore: row.stakeholderFeedbackScore ?? 0,
      stakeholderAuditScore: row.stakeholderAuditScore ?? 0,
      youthFeedbackScore: row.youthFeedbackScore ?? 0,
      employerFeedbackScore: row.employerFeedbackScore ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map(
    (report: FetchedStakeholdersFeedbackReportRequest) => ({
      id: report.id,
      region: report.region,
      funder: report.funder,
      centre: report.centre,
      centreBusinessType: report.centreBusinessType,
      state: report.state,
      district: report.district,
      cityManager: report.cityManager,
      centreStatus: report.centreStatus,
      stakeholderFeedbackScore: report.stakeholderFeedbackScore,
      stakeholderAuditScore: report.stakeholderAuditScore,
      youthFeedbackScore: report.youthFeedbackScore,
      employerFeedbackScore: report.employerFeedbackScore,
    })
  );

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedCenterVisitBranding = (
  response: FetchCenterVisitBrandingResponse
): ParsedFetchCenterVisitBrandingResponse => {
  let data: FetchedCenterVisitBrandingReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centreName: row.centerName ?? "",
      region: row.region ?? "",
      funder: row.funderName ?? "",
      centreBusinessType: row.centerBusinessType ?? "",
      state: row.state ?? "",
      district: row.district ?? "",
      cityManager: row.cityManager ?? "",
      status: row.status ?? "",
      visitDate: row.visitDate ?? "",
      staffProfessionalism: row.staffProfessionalism ?? "",
      infrastructure: row.infrastructure ?? "",
      centerHygieneAndSafety: row.centerHygieneSafety ?? "",
      centerBranding: row.centerBranding ?? "",
      remarks: row.remarks ?? "",
      total: Number(parsePercentage(row.total )) ?? 0,
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map(
    (report: FetchedCenterVisitBrandingReportRequest) => ({
      id: report.id,
      centreName: report.centreName,
      region: report.region,
      funder: report.funder,
      centreBusinessType: report.centreBusinessType,
      state: report.state,
      district: report.district,
      cityManager: report.cityManager,
      status: report.status,
      visitDate: report.visitDate,
      staffProfessionalism: report.staffProfessionalism,
      infrastructure: report.infrastructure,
      centerHygieneAndSafety: report.centerHygieneAndSafety,
      centerBranding: report.centerBranding,
      remarks: report.remarks,
      total: report.total,
    })
  );

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.fetchedStaffTraining = (
  response: FetchStaffTrainingResponse
): ParsedFetchStaffTrainingResponse => {
  let data: FetchedStaffTrainingReportRequest[] = [];

  if (
    response.success &&
    response.data &&
    Array.isArray(response.data.targetAchievements) &&
    response.data.targetAchievements.length > 0
  ) {
    data = response.data.targetAchievements.map((row: any, index: number) => ({
      id: row.id || `report-${index}`,
      centreName: row.centerName ?? "",
      region: row.region ?? "",
      funder: row.funderName ?? "",
      state: row.state ?? "",
      district: row.district ?? "",
      cityManager: row.cityManager ?? "",
      centreStatus: row.status ?? "",
      totalStaff: Number(row.totalStaff) ?? 0,
      trainingAttended:Number(row.trainingAttended )?? 0,
      attended:Number(row.attended) ?? 0,
      notAttended: Number(row.notAttended )?? 0,
      totalNotEligibleStaff: Number(row.totalNotEligibleStaff )?? 0,
      nominatedForTraining: Number(row.nominatedForTraining) ?? 0,
      totalTrainedStaff: Number(row.totalTrainedStaff) ?? 0,
      trainedStaffPercentage: Number(parsePercentage(row.trainedStaffPercentage )) ?? 0,
      overallStaffTrainedData: Number(row.overallStaffTrainedData) ?? "",
      remark: row.remark ?? "",
    }));
  }

  const total = response?.data?.totalRecords ?? 0;

  const reportRows = data.map((report: FetchedStaffTrainingReportRequest) => ({
    id: report.id,
    centreName: report.centreName,
    region: report.region,
    funder: report.funder,
    state: report.state,
    district: report.district,
    cityManager: report.cityManager,
    centreStatus: report.centreStatus,
    totalStaff: report.totalStaff,
    trainingAttended: report.trainingAttended,
    attended:report.attended,
    notAttended: report.notAttended,
    totalNotEligibleStaff: report.totalNotEligibleStaff,
    nominatedForTraining: report.nominatedForTraining,
    totalTrainedStaff: report.totalTrainedStaff,
    trainedStaffPercentage: report.trainedStaffPercentage,
    overallStaffTrainedData: report.overallStaffTrainedData,
    remark: report.remark,
  }));

  return {
    reports: reportRows,
    totalReports: total,
  };
};

reportsParser.parseManageAward = (input: ManageAwardInput): ManageAwardOutputArray => {
  if (!input?.data?.rows || input.data.rows.length === 0) {
    return [];
  }

  return input.data.rows.map((row) => ({
    id: row.manageAwardId.toString(),
    label: row.awardName,
  }));
};

export default reportsParser;
